import {Box, Indicator} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import Head from 'next/head';
import {type PropsWithChildren, useEffect, useLayoutEffect} from 'react';

// SSR時の警告を避けるためのisomorphic useLayoutEffect
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;
import {
  MdCalendarToday,
  MdCheckCircle,
  MdHelpOutline,
  MdMenu,
  MdTableChart,
} from 'react-icons/md';
import {COLOR_BLACK_1, COLOR_WHITE} from '../../../config/constants/colorCodes';
import {
  ENTRY_MANAGEMENTS_CALENDAR,
  ENTRY_MANAGEMENTS_PROGRESS,
  ENTRY_MANAGEMENTS_TODO,
} from '../../../config/constants/routes';
import {
  type ProgressMissionType,
  useGetProgressMissions,
} from '../../../core/repositories/mypageEntryManagementsRepository';
import {useIsSessionExpired} from '../../../core/usecases/auth/useIsSessionExpired';
import {cn} from '../../../lib/classname';
import {CommonLink} from '../../../shared/components/CommonLink';
import {GeneralFooter} from '../../../shared/components/Footer/general';
import {GeneralHeaderContainer} from '../../../shared/components/Header/General/container';
import {useIsAndroidWebView} from '../../../shared/hooks/useIsAndroidWebView';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {ENTRY_MANAGEMENT_FEATURE_NAME} from '../constants/metaData';
import {useEntryManagementReminders} from '../usecase/useEntryManagementReminders';
import {useMissionsVisibility} from '../usecase/useMissionsVisibility';
import {RequireAuthProvider, useRequireAuth} from '../usecase/useRequireAuth';
import {
  RequireStudentVerificationProvider,
  useRequireStudentVerification,
} from '../usecase/useRequireStudentVerification';
import {MissionsList} from './Container/Presenter/Missions/MissionsList';
import {EntryManagementTabs} from './Container/Presenter/Progress/EntryManagementTabs';
import {GuideModal} from './Container/Presenter/Progress/GuideModal';
import {GuideModalProvider, useGuideModal} from './GuideModalContext';

/**
 * サイドメニューの項目定義
 */
export type MenuItem = {
  /** 項目の一意識別子 */
  id: string;
  /** 表示ラベル */
  label: string;
  /** 遷移先のパス */
  href: string;
  /** 通常時のアイコン */
  icon: React.ReactNode;
  /** アクティブ時のアイコン */
  activeIcon: React.ReactNode;
};

/**
 * サイドメニュー項目のProps
 */
type SideMenuItemProps = {
  /** メニュー項目データ */
  item: MenuItem;
  /** アクティブ状態かどうか */
  isActive?: boolean;
  /** リスト要素としてレンダリングするか */
  isList?: boolean;
  /** メニューが折りたたまれているか */
  isCollapsed?: boolean;
  /** リマインダーバッジを表示するか */
  hasReminder?: boolean;
};

/**
 * サイドメニューのProps
 */
type SideMenuProps = {
  /** 現在選択されているメニューID */
  current: string;
  /** メニューが折りたたまれているか */
  isCollapsed: boolean;
  /** 折りたたみ状態を切り替える関数 */
  toggle: () => void;
  /** 進捗ミッションデータ */
  progressMissions?: ProgressMissionType[];
  /** ミッション読み込み中かどうか */
  isLoadingMissions?: boolean;
  /** ミッションが非表示かどうか */
  isHidden: boolean;
  /** ミッションを閉じる際のハンドラー */
  onClose: () => void;
  /** ToDoにリマインダーがあるか */
  hasTodoReminder?: boolean;
  /** 選考管理にリマインダーがあるか */
  hasProgressReminder?: boolean;
};

/**
 * サイドメニューコンテナのProps
 */
type SideMenuContainerProps = {
  /** 現在選択されているメニューID */
  current: string;
  /** メニューが折りたたまれているか */
  isCollapsed: boolean;
  /** 折りたたみ状態を切り替える関数 */
  toggle: () => void;
};

/**
 * エントリー管理レイアウトのProps
 */
type EntryManagementLayoutProps = PropsWithChildren<{
  /** 現在選択されているメニューID */
  current: string;
}>;

/**
 * サイドメニューのID定義
 * 各メニュー項目を識別するための定数
 */
export const SIDE_MENU_ID_LIST = {
  DASHBOARD: 'dashboard',
  CALENDAR: 'calendar',
  TODO: 'todo',
  PROGRESS: 'progress',
  SETTINGS: 'settings',
} as const;

/**
 * サイドメニューIDからタブ値へのマッピング
 * PC版のサイドメニューとSP版のタブを対応付ける
 */
const SIDE_MENU_ID_TO_TAB_VALUE: Record<string, string> = {
  [SIDE_MENU_ID_LIST.PROGRESS]: ENTRY_MANAGEMENTS_PROGRESS,
  [SIDE_MENU_ID_LIST.TODO]: ENTRY_MANAGEMENTS_TODO,
  [SIDE_MENU_ID_LIST.CALENDAR]: ENTRY_MANAGEMENTS_CALENDAR,
} as const;

/**
 * アイコンのサイズ定数
 */
const ICON_SIZE = 24;

/**
 * サイドメニューの上部リンク
 * 主要なナビゲーション項目を定義
 */
const SIDE_MENU_LINK_TOP: MenuItem[] = [
  {
    id: SIDE_MENU_ID_LIST.PROGRESS,
    label: '選考管理',
    href: ENTRY_MANAGEMENTS_PROGRESS,
    icon: <MdTableChart fill={COLOR_BLACK_1} size={ICON_SIZE} />,
    activeIcon: <MdTableChart fill={COLOR_WHITE} size={ICON_SIZE} />,
  },
  {
    id: SIDE_MENU_ID_LIST.TODO,
    label: 'ToDo',
    href: ENTRY_MANAGEMENTS_TODO,
    icon: <MdCheckCircle fill={COLOR_BLACK_1} size={ICON_SIZE} />,
    activeIcon: <MdCheckCircle fill={COLOR_WHITE} size={ICON_SIZE} />,
  },
  {
    id: SIDE_MENU_ID_LIST.CALENDAR,
    label: 'カレンダー',
    href: ENTRY_MANAGEMENTS_CALENDAR,
    icon: <MdCalendarToday fill={COLOR_BLACK_1} size={ICON_SIZE} />,
    activeIcon: <MdCalendarToday fill={COLOR_WHITE} size={ICON_SIZE} />,
  },
];

/**
 * サイドメニューの下部リンク
 * 設定などのサブメニュー項目（現在は未使用）
 */
const SIDE_MENU_LINK_BOTTOM: MenuItem[] = [];
// const SIDE_MENU_LINK_BOTTOM: MenuItem[] = [
//   {
//     id: SIDE_MENU_ID_LIST.SETTINGS,
//     label: '設定',
//     href: '',
//     icon: <MdSettings fill={COLOR_BLACK_1} size={ICON_SIZE} />,
//     activeIcon: <MdSettings fill={COLOR_WHITE} size={ICON_SIZE} />,
//   },
// ];

/**
 * サイドメニューの個別項目コンポーネント
 * リマインダーがある場合はラベルの横に赤いバッジを表示
 */
const SideMenuItem = ({
  item,
  isActive = false,
  isList = false,
  isCollapsed = false,
  hasReminder = false,
}: SideMenuItemProps) => {
  const Component = isList ? 'li' : 'div';

  return (
    <Component
      key={item.id}
      className={cn('rounded-4 relative', {
        'bg-primary-3': isActive,
      })}
    >
      {isCollapsed ? (
        <Indicator
          disabled={!hasReminder}
          inline
          color="red"
          position="middle-end"
          withBorder
          className="w-full"
        >
          <CommonLink
            href={item.href}
            className="py-8 flex items-center justify-center w-full px-8"
            aria-current={isActive ? 'page' : undefined}
            aria-label={item.label}
          >
            {isActive ? item.activeIcon : item.icon}
          </CommonLink>
        </Indicator>
      ) : (
        <CommonLink
          href={item.href}
          className="py-8 flex items-center w-full px-8"
          aria-current={isActive ? 'page' : undefined}
        >
          {isActive ? item.activeIcon : item.icon}
          <span
            className={cn(
              'text-base text-black-1 ml-8 flex items-center gap-4',
              {
                'text-white': isActive,
              },
            )}
          >
            {item.label}
            {hasReminder && (
              <span
                className="bg-notification rounded-full ml-4 w-[10px] h-[10px] border-[2px] border-white"
                aria-label={`${item.label}に未確認の通知があります`}
                role="status"
              />
            )}
          </span>
        </CommonLink>
      )}
    </Component>
  );
};

/**
 * メニューアイテムのリマインダー有無を判定する
 *
 * @param itemId - メニュー項目のID
 * @param hasTodoReminder - ToDoにリマインダーがあるか
 * @param hasProgressReminder - 選考管理にリマインダーがあるか
 * @returns リマインダーの有無
 */
const getMenuItemReminder = (
  itemId: string,
  hasTodoReminder: boolean,
  hasProgressReminder: boolean,
): boolean => {
  switch (itemId) {
    case SIDE_MENU_ID_LIST.TODO:
      return hasTodoReminder;
    case SIDE_MENU_ID_LIST.PROGRESS:
      return hasProgressReminder;
    default:
      return false;
  }
};

/**
 * サイドメニューコンポーネント
 * PC版のナビゲーションとして使用され、折りたたみ可能
 */
const SideMenu = ({
  current,
  isCollapsed,
  toggle,
  progressMissions,
  isLoadingMissions,
  isHidden,
  onClose,
  hasTodoReminder = false,
  hasProgressReminder = false,
}: SideMenuProps) => {
  const {open: openGuideModal} = useGuideModal();

  const shouldShowMissions =
    !isCollapsed &&
    !isHidden &&
    progressMissions &&
    progressMissions.length > 0;

  return (
    <nav
      className={cn(
        'flex flex-col justify-between shrink-0 w-[174px] bg-white rounded-16 p-8',
        {
          'w-[56px]': isCollapsed,
        },
      )}
      aria-label="エントリー管理メニュー"
    >
      <div>
        <button
          type="button"
          className="flex items-center w-full text-base p-8 mb-16"
          onClick={toggle}
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? 'メニューを展開' : 'メニューを折りたたむ'}
        >
          <MdMenu fill={COLOR_BLACK_1} size={ICON_SIZE} />
          {!isCollapsed && <span className="ml-8">メニュー</span>}
        </button>
        <ul className="flex flex-col gap-8">
          {SIDE_MENU_LINK_TOP.map((item) => {
            const hasReminder = getMenuItemReminder(
              item.id,
              hasTodoReminder,
              hasProgressReminder,
            );

            return (
              <SideMenuItem
                key={item.id}
                item={item}
                isActive={item.id === current}
                isList={true}
                isCollapsed={isCollapsed}
                hasReminder={hasReminder}
              />
            );
          })}
        </ul>
        {shouldShowMissions && (
          <div className="mt-16 pt-16 border-t border-black-5">
            <MissionsList
              missions={progressMissions}
              isLoading={isLoadingMissions}
              onClose={onClose}
            />
          </div>
        )}
        {!isCollapsed && (
          <div className="mt-8 pt-8 border-t border-black-5">
            <button
              type="button"
              className="w-full flex items-center gap-8 p-8 text-sm text-primary-3 hover:underline"
              onClick={openGuideModal}
              aria-label="操作ガイドを開く"
            >
              <MdHelpOutline size={16} />
              操作ガイド
            </button>
          </div>
        )}
      </div>
      {SIDE_MENU_LINK_BOTTOM.length > 0 && (
        <ul className="flex flex-col gap-8">
          {SIDE_MENU_LINK_BOTTOM.map((item) => (
            <SideMenuItem
              key={item.id}
              item={item}
              isActive={item.id === current}
              isList={true}
              isCollapsed={isCollapsed}
            />
          ))}
        </ul>
      )}
    </nav>
  );
};

/**
 * サイドメニューのコンテナコンポーネント
 * データフェッチとロジックを担当
 */
const SideMenuContainer = ({
  current,
  isCollapsed,
  toggle,
}: SideMenuContainerProps) => {
  const {isAuthenticated} = useRequireAuth();
  const isProgressPage = current === SIDE_MENU_ID_LIST.PROGRESS;

  const {data: progressMissionsData, isLoading: isLoadingProgressMissions} =
    useGetProgressMissions({
      config: {
        enabled: isAuthenticated && isProgressPage,
      },
    });

  const {isHidden, handleClose: handleMissionsClose} = useMissionsVisibility();
  const {hasTodoReminder, hasProgressReminder} = useEntryManagementReminders();

  // 選考管理ページでデータ取得成功時のみミッションを表示
  const missions =
    isProgressPage && progressMissionsData?.ok
      ? progressMissionsData.value
      : undefined;

  // 選考管理ページでのみローディング状態を反映
  const isLoading = isProgressPage ? isLoadingProgressMissions : false;

  /**
   * 未完了のミッション数を計算
   */
  const unCompletedMissionsCount =
    missions?.filter((mission) => !mission.completed).length ?? 0;

  /**
   * ミッションを閉じるハンドラー
   * 未完了数を記録してミッションリストを非表示にする
   */
  const handleClose = () => {
    handleMissionsClose(unCompletedMissionsCount);
  };

  return (
    <SideMenu
      current={current}
      isCollapsed={isCollapsed}
      toggle={toggle}
      progressMissions={missions}
      isLoadingMissions={isLoading}
      isHidden={isHidden}
      onClose={handleClose}
      hasTodoReminder={hasTodoReminder}
      hasProgressReminder={hasProgressReminder}
    />
  );
};

/**
 * メンテナンスモード時のオーバーレイコンポーネントのProps
 */
type MaintenanceOverlayProps = {
  /** PC版レイアウト向けに角丸をつけるか */
  rounded?: boolean;
};

/**
 * メンテナンスモード時に表示するオーバーレイ
 * 半透明の背景と中央のメッセージカードで構成される
 */
const MaintenanceOverlay = ({rounded = false}: MaintenanceOverlayProps) => {
  return (
    <>
      <div className="absolute inset-0 z-50 flex items-center justify-center">
        <div className="bg-white rounded-16 px-48 py-32 text-center shadow-lg min-w-[320px]">
          <p className="text-lg text-black-1">
            就活コパイロットはメンテナンス中です。
          </p>
          <p className="mt-8 text-base text-black-2">
            しばらくお待ちください。
          </p>
          <CommonLink
            href="/"
            className="mt-16 inline-block text-link hover:opacity-80"
          >
            トップページへ戻る
          </CommonLink>
        </div>
      </div>
      <div
        className={cn('absolute inset-0 z-40 bg-overlay-1', {
          'bg-black/50 rounded-16': rounded,
        })}
      />
    </>
  );
};

/**
 * エントリー管理レイアウトのコンテンツコンポーネント
 * PC/SPで異なるレイアウト構造を提供
 */
const EntryManagementLayoutContent = ({
  children,
  current,
}: EntryManagementLayoutProps) => {
  const [isCollapsed, {toggle: toggleIsCollapsed}] = useDisclosure(false);
  const {isPC} = useMediaQuery();
  const {opened, open, close, onAddEntryRef} = useGuideModal();
  const {isMaintenanceMode, requireAuth} = useRequireAuth();
  const {requireStudentVerification} = useRequireStudentVerification();
  const isInAppWebView = useIsAppWebView();
  const isAndroidWebView = useIsAndroidWebView();

  // メンテ中はガイドモーダルを開かない
  const openGuideModal = isMaintenanceMode ? () => {} : open;

  // サイドメニューIDをタブ値に変換（SP版で使用）
  const tabValue = SIDE_MENU_ID_TO_TAB_VALUE[current] ?? current;

  // カレンダーページではフッターを非表示
  const shouldShowFooter =
    !isInAppWebView && current !== SIDE_MENU_ID_LIST.CALENDAR;

  // SP版: タブナビゲーション、サイドバーなし
  if (!isPC) {
    if (isAndroidWebView) {
      return (
        <div className="flex flex-1 items-center justify-center h-screen bg-background-1">
          <p className="text-base text-center bg-white p-16 rounded-16">
            Android版は後日リリース予定です。
            <br />
            リリースまではブラウザ版をご利用ください。
          </p>
        </div>
      );
    }

    return (
      <>
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
          />
        </Head>
        <div>
          {!isInAppWebView && <GeneralHeaderContainer />}
          <main>
            <Box className="flex min-h-screen flex-col gap-16 items-center bg-background-1 px-8 py-12">
              <div className="relative flex flex-col gap-16 w-full">
                {isMaintenanceMode && <MaintenanceOverlay />}
                <div className="w-full bg-white rounded-[8px] flex flex-col gap-8 py-8 px-4">
                  <div className="flex items-center justify-center gap-8 px-12">
                    <p className="text-md text-black-1 font-bold">
                      {ENTRY_MANAGEMENT_FEATURE_NAME}
                    </p>
                    <button
                      type="button"
                      className="flex items-center justify-center hover:bg-black-5 transition-colors"
                      onClick={openGuideModal}
                      aria-label="操作ガイドを開く"
                    >
                      <MdHelpOutline
                        size={ICON_SIZE}
                        className="text-black-2"
                      />
                    </button>
                  </div>
                  <EntryManagementTabs value={tabValue} />
                </div>
                {children}
              </div>
            </Box>
          </main>
          {shouldShowFooter && <GeneralFooter />}
        </div>
        <GuideModal
          opened={opened && !isMaintenanceMode}
          onClose={close}
          onAddEntry={onAddEntryRef.current}
          isSP
          requireAuth={requireAuth}
          requireStudentVerification={requireStudentVerification}
        />
      </>
    );
  }

  // PC版: サイドバーナビゲーション、タブなし
  return (
    <>
      <div>
        {!isInAppWebView && <GeneralHeaderContainer />}
        <main>
          <Box
            className={cn(
              'flex min-h-screen flex-col gap-16 items-center bg-background-1 pc:px-0 py-12 pc:py-32',
            )}
          >
            <div className="relative flex w-[1200px] gap-16">
              {isMaintenanceMode && <MaintenanceOverlay rounded />}
              <SideMenuContainer
                current={current}
                isCollapsed={isCollapsed}
                toggle={toggleIsCollapsed}
              />
              <div className="flex flex-col gap-16 flex-1 overflow-x-auto">
                {children}
              </div>
            </div>
          </Box>
        </main>
      </div>
      <GuideModal
        opened={opened && !isMaintenanceMode}
        onClose={close}
        onAddEntry={onAddEntryRef.current}
        requireAuth={requireAuth}
        requireStudentVerification={requireStudentVerification}
      />
    </>
  );
};

/**
 * auth: 'optional'ページでセッション切れ状態をリセットするコンポーネント
 * このページでは期限切れCookieがあってもセッション切れ扱いにしない
 * isSessionExpiredがtrueになった場合も即座にfalseに戻す
 * useLayoutEffectを使用してSessionExpiredPopupのuseEffectより先に実行する
 */
const SessionExpiredReset = ({children}: PropsWithChildren) => {
  const {isSessionExpired, updateIsSessionExpired} = useIsSessionExpired();

  // 初回マウント時および isSessionExpired が true になった場合に即座にfalseにリセット
  // useLayoutEffectはuseEffectより先に同期的に実行されるため、
  // SessionExpiredPopupのsetTimeoutがスケジュールされる前にリセットできる
  // SSR時の警告を避けるためisomorphic版を使用
  useIsomorphicLayoutEffect(() => {
    if (isSessionExpired) {
      updateIsSessionExpired(false);
    }
  }, [isSessionExpired, updateIsSessionExpired]);

  return <>{children}</>;
};

/**
 * エントリー管理レイアウトコンポーネント
 * GuideModalProvider、RequireAuthProvider、RequireStudentVerificationProviderでラップしてモーダル機能を提供
 */
export const EntryManagementLayout = ({
  children,
  current,
}: EntryManagementLayoutProps) => {
  return (
    <SessionExpiredReset>
      <RequireAuthProvider>
        <RequireStudentVerificationProvider>
          <GuideModalProvider>
            <EntryManagementLayoutContent current={current}>
              {children}
            </EntryManagementLayoutContent>
          </GuideModalProvider>
        </RequireStudentVerificationProvider>
      </RequireAuthProvider>
    </SessionExpiredReset>
  );
};
